import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  type Operation,
} from '@apollo/client';
import {getCookie} from '@wandb/weave/common/util/cookie';
import Observable from 'zen-observable';
export {ApolloProvider} from '@apollo/client';

const makeHttpLink = (uri: string) =>
  createHttpLink({
    fetch: (input: RequestInfo, init?: RequestInit): Promise<Response> => {
      // we force using window.fetch because we need to use datadog's updated instance of window.fetch
      return window.fetch(input, init);
    },
    uri,
    // Our credentials may be a cookie on a different domain to backend
    // `api.wandb.ai` vs `wandb.ai`
    // https://www.apollographql.com/docs/react/networking/authentication/#cookie
    credentials: 'include',
  });

const authMiddleware = new ApolloLink((operation, forward) => {
  return new Observable(observer => {
    setHeader(operation, 'X-Origin', window.location.origin);
    forward(operation).subscribe(observer);
  });
});

// Adds a header specifying that an admin wants to use their
// admin privileges based on a cookie that they can manually toggle.
// Copied from frontends/app/src/util/apollo.ts
const adminPrivilegesMiddleware = new ApolloLink((operation, forward) => {
  const useAdminPrivileges = getCookie('use_admin_privileges') === 'true';
  if (useAdminPrivileges) {
    setHeader(operation, 'use-admin-privileges', 'true');
  }
  return forward!(operation);
});

// Copied from frontends/app/src/util/apollo.ts
const viewAsMiddleware = new ApolloLink((operation, forward) => {
  const useViewAs = getCookie('impersonated_username');
  if (useViewAs) {
    setHeader(operation, 'impersonated-username', useViewAs);
  }
  return forward!(operation);
});

export const makeGorillaApolloClient = (
  gorillaApolloEndpoint: string = `${window.CONFIG?.BACKEND_HOST ?? 'https://api.wandb.ai'}/graphql`
) => {
  return new ApolloClient({
    link: ApolloLink.from([
      adminPrivilegesMiddleware,
      viewAsMiddleware,
      authMiddleware,
      makeHttpLink(gorillaApolloEndpoint),
    ]),
    cache: new InMemoryCache(),
  });
};

interface OperationContext {
  headers?: {[key: string]: string};
}

function setHeader(op: Operation, key: string, value: string) {
  op.setContext(({headers = {}}: OperationContext) => ({
    headers: {
      ...headers,
      [key]: value,
    },
  }));
}
